import type { ContentPack, ContentPackItem, ContentPackPushCode, ContentPackStatus } from '../../../common/hooks/use-content-packs-api.hook';
import type { StatusTone } from '../../../components/design-system/admin';

/**
 * How a pack and its items read on the page — decided here, in plain
 * functions, so the page only lays them out. Tested in __tests__.
 */

export const SHOP_SETTINGS_ROUTE = '/business/sales/shop';
export const SOCIAL_ROUTE = '/marketing/social-scheduler';

export const PACK_STATUS_TONE: Record<ContentPackStatus, StatusTone> = {
    queued: 'neutral',
    generating: 'neutral',
    ready: 'success',
    partial: 'warning',
    failed: 'danger',
};

/** One line under an approved item: where it went, or why it did not and where to fix it. */
export interface PushLine {
    tone: StatusTone;
    /** i18n key under CONTENT_PACKS.PUSH. */
    label: string;
    labelParams?: Record<string, string>;
    /** i18n key of the explanation, when it did not go. */
    reason?: string;
    reasonParams?: Record<string, string>;
    fix?: { route: string; label: string };
    /** The item can be sent again from here. */
    retry?: boolean;
    /** The scheduled post, to open. */
    postUuid?: string;
}

const PLATFORM_NAME: Record<string, string> = {
    shopify: 'Shopify',
    prestashop: 'PrestaShop',
    woocommerce: 'WooCommerce',
    magento: 'Magento',
    bigcommerce: 'BigCommerce',
    wix: 'Wix',
    squarespace: 'Squarespace',
};

export const platformName = (p?: string) => (p ? PLATFORM_NAME[p] ?? p : '');

const SHOP_FIX: Partial<Record<ContentPackPushCode, boolean>> = { not_connected: true, unsupported_platform: true, no_publish_grant: true, not_linked: true };

/** What happened to an approved item, as lines to show. Nothing for one not approved. */
export function pushLines(item: ContentPackItem, formatDate: (iso: string) => string): PushLine[] {
    if (item.approval !== 'approved') return [];
    const out: PushLine[] = [];
    const shop = item.pushed?.shop;
    if (shop?.status === 'done') out.push({ tone: 'success', label: 'ON_SHOP' });
    else if (shop?.status === 'failed') {
        const code = shop.code || 'shop_error';
        out.push({
            tone: code === 'shop_error' ? 'danger' : 'warning',
            label: 'NOT_ON_SHOP',
            reason: `CODE.${code.toUpperCase()}`,
            reasonParams: { platform: platformName(shop.platform) },
            fix: SHOP_FIX[code] ? { route: SHOP_SETTINGS_ROUTE, label: 'FIX_SHOP' } : undefined,
            retry: code === 'shop_error' || code === 'no_image',
        });
    }
    const social = item.pushed?.social;
    if (social) {
        out.push({
            tone: 'success',
            label: social.scheduledAt ? 'SCHEDULED' : 'SCHEDULED_SHORT',
            labelParams: social.scheduledAt ? { date: formatDate(social.scheduledAt) } : undefined,
            postUuid: social.postUuid,
        });
    } else if (item.pushed?.socialPending) {
        const code = item.pushed.socialPending.code;
        out.push({
            tone: code === 'no_account' ? 'neutral' : 'danger',
            label: code === 'no_account' ? 'WAITING_ACCOUNT' : 'NOT_SCHEDULED',
            reason: `CODE.${code.toUpperCase()}`,
            fix: code === 'no_account' ? { route: SOCIAL_ROUTE, label: 'FIX_SOCIAL' } : undefined,
            retry: code !== 'no_account',
        });
    }
    return out;
}

/** The label of an item: "Room shot", "Post 2"… as an i18n key and params. */
export function itemLabel(item: Pick<ContentPackItem, 'kind' | 'key'>): { key: string; params?: Record<string, number> } {
    if (item.kind === 'post') {
        const n = Number(String(item.key).split('-')[1]);
        return Number.isFinite(n) && n > 0 ? { key: 'KIND.POST_N', params: { n } } : { key: 'KIND.POST' };
    }
    return { key: `KIND.${item.kind.toUpperCase()}` };
}

/** Pictures first (room, packshot, clip), then the posts. */
export function splitItems(pack: Pick<ContentPack, 'items'>): { pictures: ContentPackItem[]; posts: ContentPackItem[] } {
    return {
        pictures: pack.items.filter((i) => i.kind !== 'post'),
        posts: pack.items.filter((i) => i.kind === 'post'),
    };
}

/** Whether the page should keep asking: something is still being made or written. */
export function isInFlight(packs: Array<Pick<ContentPack, 'items' | 'status'>>): boolean {
    return packs.some((p) => p.status === 'queued' || p.status === 'generating' || p.items.some((i) => i.status === 'queued' || i.status === 'generating'));
}

/** A post's caption with its hashtags, the way it will be published. */
export function captionWithTags(item: Pick<ContentPackItem, 'caption' | 'hashtags'>): string {
    const tags = (item.hashtags || []).map((h) => `#${h}`).join(' ');
    return [item.caption || '', tags].filter(Boolean).join('\n\n');
}
